/**
 * AI 服务 - 调用后端 API 进行翻译评估
 */

interface TranslationEvaluation {
  score: number;           // 0-100 分
  feedback: string;        // 评价反馈
  reference: string;       // 参考翻译
  improvements: string[];  // 改进建议
}

interface EvaluationRequest {
  originalText: string;    // 原文
  userTranslation: string; // 用户翻译
  context?: string;        // 上下文（前面层级的翻译）
  level: number;           // 当前层级
}

// 后端 API 地址
// 生产环境使用 BASE_URL（虚拟目录路径），开发环境使用 localhost
const getApiBase = () => {
  let url: string;
  if (import.meta.env.VITE_BACKEND_URL) {
    url = import.meta.env.VITE_BACKEND_URL;
  } else if (import.meta.env.PROD) {
    url = import.meta.env.BASE_URL;
  } else {
    url = 'http://localhost:8001';
  }
  // 确保以斜杠结尾
  return url.endsWith('/') ? url : url + '/';
};

const API_BASE = getApiBase();

/**
 * 调用后端 API 评估翻译
 */
export async function evaluateTranslation(request: EvaluationRequest): Promise<TranslationEvaluation> {
  try {
    const response = await fetch(`${API_BASE}api/ai/evaluate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        originalText: request.originalText,
        userTranslation: request.userTranslation,
        level: request.level,
        context: request.context || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `API 错误: ${response.status}`);
    }

    const result = await response.json();
    
    return {
      score: Math.min(100, Math.max(0, result.score || 0)),
      feedback: result.feedback || '评估完成',
      reference: result.reference || '',
      improvements: result.improvements || [],
    };
  } catch (error) {
    console.error('AI 评估失败:', error);
    throw error;
  }
}

// 完整评估结果（包含每层评分和综合评分）
interface FullEvaluationResult {
  layerScores: {
    level: number;
    score: number;
    feedback: string;
    reference: string;
  }[];
  overall: {
    score: number;
    feedback: string;
    reference: string;
    improvements: string[];
  };
}

/**
 * 统一评估所有翻译层级（返回每层评分 + 综合评分）
 * @param fullSentence 完整原句
 * @param layers 各层翻译信息
 */
export async function evaluateAllTranslations(
  fullSentence: string,
  layers: { currentLevelText: string; userTranslation: string; level: number }[]
): Promise<FullEvaluationResult> {
  try {
    const response = await fetch(`${API_BASE}api/ai/evaluate-all`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fullSentence,
        layers,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `API 错误: ${response.status}`);
    }

    const result = await response.json();
    
    return {
      layerScores: (result.layerScores || []).map((ls: { level: number; score: number; feedback: string; reference: string }) => ({
        level: ls.level,
        score: Math.min(100, Math.max(0, ls.score || 0)),
        feedback: ls.feedback || '',
        reference: ls.reference || '',
      })),
      overall: {
        score: Math.min(100, Math.max(0, result.overall?.score || 0)),
        feedback: result.overall?.feedback || '评估完成',
        reference: result.overall?.reference || '',
        improvements: result.overall?.improvements || [],
      },
    };
  } catch (error) {
    console.error('AI 评估失败:', error);
    throw error;
  }
}

/**
 * 检查 API 是否配置
 */
export async function isAIConfigured(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}api/ai/configured`);
    if (!response.ok) return false;
    const result = await response.json();
    return result.configured === true;
  } catch {
    return false;
  }
}

// 综合评价结果
import { ComprehensiveEvaluation, StepRecord } from '../types';

interface ComprehensiveEvaluationRequest {
  sentence_text: string;
  steps: {
    step_num: number;
    score: number;
    attempts: number;
    duration_seconds: number;
  }[];
  translation_feedback?: string;
}

/**
 * 获取练习综合评价
 * @param sentenceText 练习句子原文
 * @param steps 五个步骤的练习成绩
 * @param translationFeedback 翻译环节的AI评价（可选）
 */
export async function getComprehensiveEvaluation(
  sentenceText: string,
  steps: StepRecord[],
  translationFeedback?: string
): Promise<ComprehensiveEvaluation> {
  try {
    // 转换步骤数据格式
    const stepsData = steps.map(step => ({
      step_num: step.step_num,
      score: step.score || 0,
      attempts: step.attempts,
      duration_seconds: step.duration_seconds || 0,
    }));

    const requestBody: ComprehensiveEvaluationRequest = {
      sentence_text: sentenceText,
      steps: stepsData,
      translation_feedback: translationFeedback,
    };

    const response = await fetch(`${API_BASE}api/ai/comprehensive-evaluation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `API 错误: ${response.status}`);
    }

    const result = await response.json();
    
    return {
      overallAnalysis: result.overallAnalysis || '综合评价加载中...',
      stepHighlights: result.stepHighlights || [],
      improvements: result.improvements || [],
      studyFocus: result.studyFocus || '继续练习',
    };
  } catch (error) {
    console.error('获取综合评价失败:', error);
    // 返回默认值而不是抛出错误，保证用户体验
    return {
      overallAnalysis: 'AI 综合评价暂时不可用。请根据各步骤得分进行针对性练习。',
      stepHighlights: ['过程识别：暂无评价', '对象识别：暂无评价', '建立连接：暂无评价', '句子拆解：暂无评价', '逐层翻译：暂无评价'],
      improvements: ['建议多练习长难句分析', '加强语法结构理解'],
      studyFocus: '继续练习，提升综合分析能力',
    };
  }
}